import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchPendingWork } from '../../lib/api/activity';
import type { PendingWorkItem, WorkStatusKind } from '../../../../shared/lib/workStatus';

/**
 * Pending-work banner for the epic detail page (FORGE-087).
 *
 * Shows every wedge on THIS epic that has no live session behind it:
 * operator actions pending (with the runbook), startup crashes, launch
 * failures, escalations, blocks, stalls — the things that used to be
 * invisible (CloudWatch/GitHub-comment only) while the page rendered silence.
 *
 * Items are split into two groups:
 *  - "Needs attention" (amber): actionable problems — crashes, failures,
 *    escalations, blocks, and stalls in a non-PENDING state (i.e. the story
 *    has already started and is now stuck).
 *  - "Queued behind dependencies" (slate/blue): stalled stories still in
 *    PENDING state — they haven't started yet, almost certainly because an
 *    upstream story hasn't finished. Not actionable; shown for visibility.
 *
 * `queued` and `awaiting_approval` are excluded: queued is healthy, and
 * PLAN_POSTED already renders the PlanReviewCard.
 */

const SHOWN: ReadonlySet<WorkStatusKind> = new Set([
  'operator_action_required', 'escalated', 'crashed_at_startup',
  'launch_failed', 'blocked', 'stalled',
]);

const LABEL: Partial<Record<WorkStatusKind, string>> = {
  operator_action_required: 'Operator action required',
  escalated: 'Escalated',
  crashed_at_startup: 'Crashed at startup',
  launch_failed: 'Launch failed',
  blocked: 'Blocked',
  stalled: 'Stalled',
};

/**
 * A stalled story in PENDING state hasn't started yet — it's waiting on an
 * upstream dependency, not genuinely stuck. Everything else is actionable.
 */
function isDependencyWait(it: PendingWorkItem): boolean {
  return it.status === 'stalled' && it.state === 'PENDING';
}

function waitingLabel(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h}h ${mins % 60}m` : `${Math.floor(h / 24)}d ${h % 24}h`;
}

const ItemRow: React.FC<{ it: PendingWorkItem; textCls: string; mutedCls: string; codeCls: string }> = (
  { it, textCls, mutedCls, codeCls },
) => (
  <li className={`text-xs ${textCls}`}>
    <span className="font-semibold">{LABEL[it.status] ?? it.status}</span>
    {it.storyId && <span className="font-mono"> · story {it.storyId}</span>}
    <span className={mutedCls}> · {waitingLabel(it.minutesInState)} in {it.state}</span>
    {it.reason && <div className="mt-0.5">{it.reason}</div>}
    {it.runbook && (
      <code className={`block font-mono text-2xs rounded px-1.5 py-0.5 mt-1 overflow-x-auto whitespace-nowrap ${codeCls}`}>
        {it.runbook}
      </code>
    )}
  </li>
);

export const PendingWorkBanner: React.FC<{ epicRef: string }> = ({ epicRef }) => {
  const query = useQuery({
    queryKey: ['activity-pending'],
    queryFn: fetchPendingWork,
    staleTime: 15_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });

  const all = (query.data?.items ?? []).filter(
    (it: PendingWorkItem) => it.epicRef === epicRef && SHOWN.has(it.status),
  );
  if (all.length === 0) return null;

  const urgent   = all.filter((it: PendingWorkItem) => !isDependencyWait(it));
  const waiting  = all.filter((it: PendingWorkItem) =>  isDependencyWait(it));

  return (
    <div data-testid="epic-pending-banner" className="space-y-2">
      <div className="flex justify-end">
        <Link
          to="/today"
          data-testid="epic-pending-banner-see-all"
          className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline"
        >
          See all in Today →
        </Link>
      </div>
      {urgent.length > 0 && (
        <div
          role="alert"
          className="border border-amber-300 bg-amber-50 rounded-xl px-4 py-3 space-y-2"
        >
          <div className="text-sm font-semibold text-amber-900">
            ⏸ {urgent.length === 1 ? 'This epic needs attention' : `${urgent.length} items need attention`}
          </div>
          <ul className="space-y-1.5">
            {urgent.map((it: PendingWorkItem) => (
              <ItemRow
                key={it.id} it={it}
                textCls="text-amber-900"
                mutedCls="text-amber-700"
                codeCls="bg-white/70 border border-amber-200"
              />
            ))}
          </ul>
        </div>
      )}

      {waiting.length > 0 && (
        <div className="border border-slate-200 bg-slate-50 rounded-xl px-4 py-3 space-y-2">
          <div className="text-sm font-semibold text-slate-600">
            ⏳ {waiting.length === 1 ? '1 story queued behind dependencies' : `${waiting.length} stories queued behind dependencies`}
          </div>
          <ul className="space-y-1.5">
            {waiting.map((it: PendingWorkItem) => (
              <ItemRow
                key={it.id} it={it}
                textCls="text-slate-700"
                mutedCls="text-slate-500"
                codeCls="bg-white border border-slate-200"
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PendingWorkBanner;
